import { useLocale } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

import type { Amount, Currency, IsoDate, IsoDateTime } from '@/api/common.ts';
import { daysBetween, getToday } from '@/utils/dates.ts';

// Parsed as a local date: `new Date('2026-09-01')` would be UTC midnight and can shift to the previous day.
const parseIsoDate = (date: IsoDate) => new Date(`${date}T00:00:00`);

// Amounts are minor units; the currency decides how many there are per major unit (EUR: 100, JPY: 1).
export const getMinorUnitFactor = (currency: Currency) =>
    10 ** (new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2);

// Each unit with its length in seconds, the largest first.
const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 365 * 24 * 60 * 60],
    ['month', 30 * 24 * 60 * 60],
    ['week', 7 * 24 * 60 * 60],
    ['day', 24 * 60 * 60],
    ['hour', 60 * 60],
    ['minute', 60],
];

const useFormat = () => {
    const { locale } = useLocale();
    const { t } = useTranslation();

    const formatAmount = (amount: Amount, currency: Currency) =>
        new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount / getMinorUnitFactor(currency));

    const formatDate = (date: IsoDate) =>
        new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(parseIsoDate(date));

    const formatDateRange = (start: IsoDate, end: IsoDate) =>
        new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).formatRange(
            parseIsoDate(start),
            parseIsoDate(end)
        );

    const formatLongDate = (date: IsoDate) =>
        new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(
            parseIsoDate(date)
        );

    // "Heute", "Morgen", "Gestern" or the day with its weekday; lowercase within a sentence ("fällig heute").
    const formatDay = (date: IsoDate, { isInSentence = false }: { isInSentence?: boolean } = {}) => {
        const days = daysBetween(getToday(), date);
        const key = days === 0 ? 'today' : days === 1 ? 'tomorrow' : days === -1 ? 'yesterday' : null;
        if (!key) return formatWeekdayDate(date);
        return isInSentence ? t(`common.dates.inSentence.${key}`) : t(`common.dates.${key}`);
    };

    // Only the weekday, where the date shows elsewhere: "Dienstag".
    const formatWeekday = (date: IsoDate) =>
        new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(parseIsoDate(date));

    // A day near today, with its weekday: "Di., 20. Okt.".
    const formatWeekdayDate = (date: IsoDate) =>
        new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(
            parseIsoDate(date)
        );

    const formatMonth = (date: IsoDate) =>
        new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(parseIsoDate(date));

    const formatDateTime = (dateTime: IsoDateTime) =>
        new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(dateTime));

    // A date's month in three letters, as formatShortDate writes it ("Okt", "Oct").
    const formatShortMonth = (date: IsoDate) =>
        new Intl.DateTimeFormat(locale, { month: 'long' }).format(parseIsoDate(date)).slice(0, 3);

    // The day of a moment with its month in three letters ("7. Okt", "Oct 7"), which Intl's short months aren't in
    // every language ("Sept.", "März"); the year only when it isn't this one.
    const formatShortDate = (dateTime: IsoDateTime) => {
        const date = new Date(dateTime);
        const isThisYear = date.getFullYear() === new Date().getFullYear();
        return new Intl.DateTimeFormat(locale, {
            day: 'numeric',
            month: 'long',
            year: isThisYear ? undefined : 'numeric',
        })
            .formatToParts(date)
            .map((part) => (part.type === 'month' ? part.value.slice(0, 3) : part.value))
            .join('');
    };

    // A date as formatShortDate writes a moment's ("7. Okt"), at its own midnight rather than UTC's, so it's the same day
    // everywhere.
    const formatShortDay = (date: IsoDate) => formatShortDate(`${date}T00:00:00`);

    // A time of day as the API sends it, `10:00:00`.
    const formatTime = (time: string) =>
        new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(`1970-01-01T${time}`));

    // How long ago or until, in the largest unit that fits: "vor 5 Minuten", "gestern", "letzte Woche"; "jetzt" within a
    // minute.
    const formatRelativeDateTime = (dateTime: IsoDateTime) => {
        const seconds = (new Date(dateTime).getTime() - Date.now()) / 1000;
        const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        const unit = RELATIVE_UNITS.find(([, size]) => Math.abs(seconds) >= size);
        return unit ? format.format(Math.round(seconds / unit[1]), unit[0]) : format.format(0, 'second');
    };

    // The time of day of a moment, in the browser's time zone: "19:22".
    const formatTimeOf = (dateTime: IsoDateTime) =>
        new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(dateTime));

    const formatLongDateTime = (dateTime: IsoDateTime) =>
        new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short' }).format(new Date(dateTime));

    const formatPercent = (percent: number) =>
        new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 }).format(percent / 100);

    const formatNumber = (value: number, maximumFractionDigits = 4) =>
        new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);

    // "340 kB", "1,2 MB"
    const formatFileSize = (bytes: number) => {
        const isMegabytes = bytes >= 1000 * 1000;
        return new Intl.NumberFormat(locale, {
            style: 'unit',
            unit: isMegabytes ? 'megabyte' : 'kilobyte',
            maximumFractionDigits: isMegabytes ? 1 : 0,
        }).format(Math.max(bytes / (isMegabytes ? 1000 * 1000 : 1000), isMegabytes ? 0 : 1));
    };

    return {
        formatAmount,
        formatDate,
        formatLongDate,
        formatWeekday,
        formatWeekdayDate,
        formatDay,
        formatDateRange,
        formatMonth,
        formatDateTime,
        formatTime,
        formatTimeOf,
        formatShortDate,
        formatShortDay,
        formatShortMonth,
        formatRelativeDateTime,
        formatLongDateTime,
        formatPercent,
        formatNumber,
        formatFileSize,
    };
};

export default useFormat;
